/**
 * task-link.ts — the Aufgaben → Rechnungskontrolle deep-link contract (Story UI-U4).
 *
 * An invoice task (`subject_type === 'invoice'`, one of the four invoice
 * processes) maps to `/rechnungen?invoice=<subject_id>` — the peek IS the
 * decision surface (no duplicated inline decision block). The sibling Aufgaben
 * story consumes the SAME helper/URL contract, so the join key lives here once.
 *
 * [Source: apps/api/src/modules/invoice-check/invoice-check.constants.ts
 *          (INVOICE_MATCH / K2_REVIEW / K3_ESCALATION task processes;
 *           subject_type='invoice', subject_id=invoiceId)]
 * [Source: docs/stories/ui-u4-rechnungskontrolle.md AC5]
 */

import type { TaskProcess } from '@brokly/shared';

/** The four task processes that target an invoice (all carry subject_type='invoice'). */
const INVOICE_TASK_PROCESSES: ReadonlySet<TaskProcess> = new Set<TaskProcess>([
  'invoice_manual_match',
  'invoice_k2_review',
  'invoice_k3_escalation',
  'invoice_check',
]);

/** The router target for an invoice task's "Rechnung öffnen" affordance. */
export interface InvoiceTaskLink {
  to: '/rechnungen';
  search: { invoice: string };
}

/**
 * Map a task to its Rechnungen deep-link, or null when it is not an invoice task.
 * Both signals are required: an invoice `process` AND `subject_type === 'invoice'`
 * with a `subject_id` (the invoice id).
 */
export function invoiceTaskLink(
  task: Pick<
    { process: TaskProcess; subject_type: string | null; subject_id: string | null },
    'process' | 'subject_type' | 'subject_id'
  >,
): InvoiceTaskLink | null {
  if (task.subject_type !== 'invoice' || !task.subject_id) return null;
  if (!INVOICE_TASK_PROCESSES.has(task.process)) return null;
  return { to: '/rechnungen', search: { invoice: task.subject_id } };
}
